import React, { useEffect, useRef, useState } from "react";
import { CustomIcon } from "../../shared/CustomIcon";

// ============================================================
// SenderScreen — Input + session history with inline edit for the
// last 10 phrases. Receives socket state via props.
// ============================================================

export default function SenderScreen({
  connected,
  phrases,
  me,
  roster,
  sendPhrase,
  editPhrase,
  clearScreen,
  promote,
  demote,
  promotedSenders,
  onSwitchRole,
}) {
  const [text, setText] = useState("");
  const [translation, setTranslation] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editTranslation, setEditTranslation] = useState("");
  const [showRoster, setShowRoster] = useState(false);
  const inputRef = useRef(null);

  // Autofocus the input on mount & after every send
  useEffect(() => { inputRef.current?.focus(); }, []);

  const trySend = (e) => {
    if (e) e.preventDefault();
    const t = text.trim();
    const tr = translation.trim();
    if (!t && !tr) return;
    sendPhrase(t, tr);
    setText("");
    setTranslation("");
    inputRef.current?.focus();
  };

  const beginEdit = (p) => {
    setEditingId(p.id);
    setEditText(p.text || "");
    setEditTranslation(p.translation || "");
  };
  const commitEdit = () => {
    if (!editingId) return;
    editPhrase(editingId, editText.trim(), editTranslation.trim());
    setEditingId(null);
  };
  const cancelEdit = () => { setEditingId(null); };

  // Last 10 phrases are editable; older ones display without an edit icon.
  const recent = phrases.slice(-10);
  const olderCount = Math.max(0, phrases.length - recent.length);

  const connectedCount = roster.length;

  return (
    <div data-testid="translation-sender-screen" style={{ display: "flex", flexDirection: "column", height: "100%", gap: 12 }}>
      {/* STATUS BAR */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px",
          background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
          border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)",
          borderRadius: 10,
          flexWrap: "wrap",
        }}
      >
        <StatusDot connected={connected} />
        <span style={{ fontWeight: 700, fontSize: 13 }} data-testid="translation-sender-status">
          {connected ? "Live" : "Reconnecting…"}
        </span>
        <span style={{ fontSize: 12, opacity: 0.75, marginLeft: 6 }} data-testid="translation-sender-roster-count">
          {connectedCount} connected
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => setShowRoster(v => !v)}
          data-testid="translation-sender-roster-toggle"
        >
          <CustomIcon name="people" size={12} style={{ marginRight: 4 }} /> Roster
        </button>
        {me?.canPromote && (
          <span style={{ fontSize: 10, opacity: 0.7, letterSpacing: 0.5, textTransform: "uppercase" }}>
            Leader
          </span>
        )}
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={onSwitchRole}
          data-testid="translation-sender-switch-role"
          title="Switch between Sender / Receiver / Observer"
        >Switch Role</button>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => { if (window.confirm("Clear the receiver screen for everyone?")) clearScreen(); }}
          data-testid="translation-sender-clear"
          style={{ color: "#f87171", borderColor: "#f87171" }}
        >Clear Screen</button>
      </div>

      {/* ROSTER PANEL (collapsible) */}
      {showRoster && (
        <div
          data-testid="translation-sender-roster-panel"
          style={{
            padding: 10,
            background: "color-mix(in srgb, var(--kc-text) 4%, transparent)",
            borderRadius: 10,
          }}
        >
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, opacity: 0.65 }}>
            Session Roster
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {roster.map(r => (
              <div
                key={r.userId}
                data-testid={`translation-sender-roster-row-${r.userId}`}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 6, background: "rgba(0,0,0,0.2)" }}
              >
                <span style={{ fontWeight: 600, flex: 1 }}>{r.name}</span>
                <span
                  style={{
                    padding: "2px 8px", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5,
                    borderRadius: 999, fontWeight: 700,
                    background: r.role === "sender" ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-text) 15%, transparent)",
                    color: r.role === "sender" ? "#111" : "var(--kc-text)",
                  }}
                >{r.role}</span>
                {me?.canPromote && r.userId !== me.yourUserId && r.role !== "sender" && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => promote(r.userId)}
                    data-testid={`translation-sender-promote-${r.userId}`}
                  >Promote</button>
                )}
                {me?.canPromote && r.userId !== me.yourUserId && r.role === "sender" && promotedSenders.includes(r.userId) && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => demote(r.userId)}
                    data-testid={`translation-sender-demote-${r.userId}`}
                  >Demote</button>
                )}
              </div>
            ))}
            {roster.length === 0 && (
              <div style={{ fontSize: 12, opacity: 0.6 }}>No one else connected.</div>
            )}
          </div>
        </div>
      )}

      {/* INPUT CARD */}
      <form onSubmit={trySend} style={{
        padding: 16,
        background: "color-mix(in srgb, var(--kc-accent) 4%, transparent)",
        border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)",
        borderRadius: 12,
      }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 220px", minWidth: 180 }}>
            <label className="form-label" style={{ fontSize: 11 }}>Source word / phrase</label>
            <input
              ref={inputRef}
              type="text"
              className="form-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Grace"
              data-testid="translation-sender-input-text"
              style={{ fontSize: 18 }}
              lang="auto"
              autoComplete="off"
            />
          </div>
          <div style={{ flex: "1 1 220px", minWidth: 180 }}>
            <label className="form-label" style={{ fontSize: 11 }}>Translation</label>
            <input
              type="text"
              className="form-input"
              value={translation}
              onChange={(e) => setTranslation(e.target.value)}
              placeholder="e.g. Grâce"
              data-testid="translation-sender-input-translation"
              style={{ fontSize: 18 }}
              lang="auto"
              autoComplete="off"
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
          <div style={{ fontSize: 11, opacity: 0.6, flex: 1 }}>
            Press <b>Enter</b> to send. Keep phrases short (under 60 characters) for best legibility.
          </div>
          <button
            type="submit"
            className="btn"
            disabled={!connected || (!text.trim() && !translation.trim())}
            data-testid="translation-sender-send"
            style={{
              background: "var(--kc-accent)",
              color: "#111",
              fontWeight: 800,
              minHeight: 44,
              padding: "0 24px",
              borderRadius: 10,
              fontSize: 14,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              cursor: (!connected || (!text.trim() && !translation.trim())) ? "not-allowed" : "pointer",
              opacity: (!connected || (!text.trim() && !translation.trim())) ? 0.5 : 1,
            }}
          >Send</button>
        </div>
      </form>

      {/* SESSION HISTORY */}
      <div style={{
        flex: 1,
        padding: 12,
        borderRadius: 12,
        background: "rgba(0,0,0,0.25)",
        border: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)",
        overflowY: "auto",
      }} data-testid="translation-sender-history">
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, opacity: 0.65 }}>
          Session History ({phrases.length})
        </div>
        {olderCount > 0 && (
          <div style={{ fontSize: 11, opacity: 0.5, marginBottom: 6 }}>
            {olderCount} older phrase{olderCount === 1 ? "" : "s"} hidden (edit disabled)
          </div>
        )}
        {phrases.length === 0 && (
          <div style={{ fontSize: 12, opacity: 0.5 }}>No phrases sent yet.</div>
        )}
        {recent.map(p => {
          const isEditing = editingId === p.id;
          return (
            <div
              key={p.id}
              data-testid={`translation-sender-history-row-${p.id}`}
              style={{
                padding: "8px 10px",
                marginBottom: 6,
                borderRadius: 8,
                background: isEditing ? "color-mix(in srgb, var(--kc-accent) 12%, transparent)" : "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              {!isEditing ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {p.text || <i style={{ opacity: 0.5 }}>(no source)</i>}
                      <span style={{ opacity: 0.5 }}> / </span>
                      <span style={{ color: "var(--kc-accent)", fontWeight: 700 }}>{p.translation || <i style={{ opacity: 0.5 }}>(no translation)</i>}</span>
                      {p.edited && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--kc-amber,#D4AF37)" }}>EDITED</span>}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.5 }}>
                      {new Date(p.timestamp || Date.now()).toLocaleTimeString()} · {p.sentByName || "—"}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => beginEdit(p)}
                    data-testid={`translation-sender-edit-${p.id}`}
                    title="Edit this phrase"
                    aria-label="Edit"
                    style={{ padding: "4px 8px" }}
                  >
                    <CustomIcon name="document" size={11} />
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <input
                    className="form-input"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    data-testid={`translation-sender-edit-text-${p.id}`}
                    placeholder="Source"
                  />
                  <input
                    className="form-input"
                    value={editTranslation}
                    onChange={(e) => setEditTranslation(e.target.value)}
                    data-testid={`translation-sender-edit-translation-${p.id}`}
                    placeholder="Translation"
                  />
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button type="button" className="btn btn-outline btn-sm" onClick={cancelEdit} data-testid={`translation-sender-edit-cancel-${p.id}`}>Cancel</button>
                    <button type="button" className="btn btn-sm" onClick={commitEdit} data-testid={`translation-sender-edit-update-${p.id}`} style={{ background: "var(--kc-accent)", color: "#111", fontWeight: 700 }}>Update</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusDot({ connected }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 10, height: 10,
        borderRadius: "50%",
        background: connected ? "#4ade80" : "#f87171",
        boxShadow: `0 0 8px ${connected ? "#4ade80" : "#f87171"}`,
      }}
    />
  );
}
